/**
 * `MaeAdvice` — l'avviso che porta con sé il rimedio.
 *
 * La regola, rubata all'editor delle assenze del FE Lares: quando la UI segnala un
 * ostacolo, il controllo che lo rimuove sta **dentro** l'avviso, non altrove nella pagina.
 * Il loro caso di scuola è il preavviso minimo — invece di scrivere "sei sotto il preavviso
 * di 5 giorni" e lasciare l'utente a cercare da solo la select "Priorità" trenta pixel più
 * su, mettono la casella «contrassegna come urgente» nel banner stesso. L'utente legge il
 * problema e lo risolve senza spostare gli occhi.
 *
 * Da qui il vincolo di tipo: serve `action` **oppure** `control`. Un `MaeAdvice` di tono
 * `warning`/`danger` senza via d'uscita è esattamente l'avviso inerte che stiamo cercando
 * di eliminare. Il tono `info` invece è puramente esplicativo e non chiede nulla.
 *
 * ```tsx
 * <MaeAdvice
 *   tone="warning"
 *   title={`Sei sotto il preavviso minimo di ${minAdvanceDays} giorni`}
 *   control={
 *     <Checkbox
 *       label="Contrassegna come richiesta urgente"
 *       checked={priority === 'urgent'}
 *       onChange={(e) => setPriority(e.currentTarget.checked ? 'urgent' : 'normal')}
 *     />
 *   }
 * >
 *   Per questo tipo di assenza servono {minAdvanceDays} giorni di anticipo. Se la
 *   contrassegni come urgente, l’HR potrà valutarla comunque con nota di giustificazione.
 * </MaeAdvice>
 * ```
 */
import type { ReactNode } from 'react';
import { Alert, Button, Group, Stack, Text } from '@mantine/core';
import {
  IconAlertTriangle,
  IconExclamationCircle,
  IconInfoCircle,
  type IconProps,
} from '@tabler/icons-react';
import type { ComponentType } from 'react';

export type MaeAdviceTone = 'info' | 'warning' | 'danger';

const TONE: Record<MaeAdviceTone, { color: string; icon: ComponentType<IconProps> }> = {
  info: { color: 'azzurro', icon: IconInfoCircle },
  warning: { color: 'yellow', icon: IconAlertTriangle },
  danger: { color: 'red', icon: IconExclamationCircle },
};

interface Base {
  /** Il problema in una riga. */
  title?: string;
  /** Spiegazione: cosa comporta e cosa succede se l'utente agisce. */
  children: ReactNode;
  tone?: MaeAdviceTone;
  /** Icona alternativa a quella del tono. */
  icon?: ComponentType<IconProps>;
}

/** Il rimedio come bottone (naviga, apre una modale, applica una correzione). */
interface WithAction extends Base {
  action: { label: string; onClick: () => void };
  control?: never;
  selfRemedy?: never;
}

/** Il rimedio come controllo vero e proprio, renderizzato dentro il banner. */
interface WithControl extends Base {
  control: ReactNode;
  action?: never;
  selfRemedy?: never;
}

/**
 * Il rimedio esiste ma non è un bottone: sta in un campo della pagina, in un'altra
 * sezione, o passa da una persona. In quei casi obbligare un `action` produrrebbe bottoni
 * finti; qui si scrive **dove** si risolve, che è l'informazione che manca davvero.
 * Es. «Accorcia il periodo nel campo qui sopra» — non «Riprova».
 */
interface WithSelfRemedy extends Base {
  selfRemedy: string;
  action?: never;
  control?: never;
}

/** Solo spiegazione: ammesso unicamente sul tono `info`, che non chiede nulla. */
interface Informational extends Base {
  tone: 'info';
  action?: never;
  control?: never;
  selfRemedy?: never;
}

export type MaeAdviceProps = WithAction | WithControl | WithSelfRemedy | Informational;

export function MaeAdvice(props: MaeAdviceProps) {
  const { title, children, tone = 'info', icon } = props;
  const action = 'action' in props ? props.action : undefined;
  const control = 'control' in props ? props.control : undefined;
  const selfRemedy = 'selfRemedy' in props ? props.selfRemedy : undefined;
  const { color, icon: ToneIcon } = TONE[tone];
  const Icon = icon ?? ToneIcon;

  return (
    /* `mae-advice`: la classe con cui `theme.css` dà il margine sotto all'avviso messo
       direttamente nella pagina. Vedi là il perché. */
    <Alert className="mae-advice" color={color} variant="light" icon={<Icon size={17} />} title={title} p="sm">
      <Stack gap={8}>
        {/*
          ⚠️ **`component="div"` e non il `<p>` predefinito di `Text`.**

          Il 30-09-2026 la console diceva «*In HTML, `<p>` cannot be a descendant of `<p>`. This
          will cause a hydration error*»: chi passa un blocco come figlio — e sono **48 usi su
          156** — ne infilava uno dentro l'altro. Non è un cavillo: il browser **chiude** il `<p>`
          esterno quando ne incontra uno interno, quindi l'albero che React ha in mente e quello
          che il browser costruisce divergono, e in idratazione si rompe.

          Il rimedio giusto sta qui e non nei 48 punti di chiamata: passare un paragrafo a un
          avviso è legittimo, ed era questo contenitore a non poterlo contenere. L'aspetto non
          cambia — `Text` non ha margini propri, cambia solo il tag.
        */}
        <Text component="div" size="sm" style={{ lineHeight: 1.55 }}>{children}</Text>
        {control}
        {selfRemedy && <Text size="sm" fw={600} style={{ lineHeight: 1.55 }}>{selfRemedy}</Text>}
        {action && (
          <Group gap={8}>
            <Button size="compact-sm" variant="light" color={color} onClick={action.onClick}>
              {action.label}
            </Button>
          </Group>
        )}
      </Stack>
    </Alert>
  );
}
